import React, { useEffect, useRef, useState } from "react";
import api from "../../api"; // Axios instance with baseURL and headers
import HrLayout from "../HrLayout";

export default function HrEmployeeBank() {
  const [employees, setEmployees] = useState([]);
  const [employeeId, setEmployeeId] = useState("");
  const [bank, setBank] = useState({
    account_holder: "",
    bank_name: "",
    account_no: "",
    ifsc: "",
    upi_id: "",
    branch_name: "",
  });
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState("");

  // 🔽 searchable select states
  const [open, setOpen] = useState(true); // auto open
  const [search, setSearch] = useState("");
  const dropdownRef = useRef(null);

  /* ================= LOAD EMPLOYEES ================= */
  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const res = await api.get("/employees");
        setEmployees(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        console.error("Employee load error:", err.response?.data || err);
        setMsg("error: Failed to load employees");
      }
    };
    fetchEmployees();
  }, []);

  /* ================= LOAD BANK DETAILS ================= */
  const loadBank = async (empId) => {
    if (!empId) return;
    try {
      setLoading(true);
      const res = await api.get(`/banks?employee_id=${empId}`);
      if (res.data) {
        setBank({
          account_holder: res.data.account_holder || "",
          bank_name: res.data.bank_name || "",
          account_no: res.data.account_no || "",
          ifsc: res.data.ifsc || "",
          upi_id: res.data.upi_id || "",
          branch_name: res.data.branch_name || "",
        });
      } else {
        resetBank();
      }
    } catch (err) {
      console.error("Load bank error:", err.response?.data || err);
      setMsg("error: Failed to load bank details");
      resetBank();
    } finally {
      setLoading(false);
    }
  };

  /* ================= SAVE BANK ================= */
  const saveBank = async (e) => {
    e.preventDefault();
    if (!employeeId) {
      alert("Select an employee first");
      return;
    }

    try {
      const payload = {
        employee_id: Number(employeeId),
        ...bank,
      };

      const res = await api.post("/banks", payload);
      if (res.status === 200) {
        setMsg("success: Bank details saved successfully");
      } else {
        setMsg("error: Failed to save bank details");
      }
    } catch (err) {
      console.error("Save bank error:", err.response?.data || err);
      setMsg("error: Failed to save bank details");
    }
  };

  /* ================= RESET BANK FORM ================= */
  const resetBank = () => {
    setBank({
      account_holder: "",
      bank_name: "",
      account_no: "",
      ifsc: "",
      upi_id: "",
      branch_name: "",
    });
  };

  /* ================= FILTER EMPLOYEES ================= */
  const filteredEmployees = employees.filter(emp =>
    `${emp.first_name} ${emp.last_name} ${emp.id}`
      .toLowerCase()
      .includes(search.toLowerCase())
  );

  const selectedEmployee = employees.find(
    e => e.id === Number(employeeId)
  );

  /* ================= CLOSE DROPDOWN ON OUTSIDE CLICK ================= */
  useEffect(() => {
    const handler = e => {
      if (!dropdownRef.current?.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <HrLayout>
      <div className="max-w-4xl mx-auto bg-white p-6 rounded-xl shadow border">
        <h2 className="text-2xl font-semibold mb-5">
          HR – Manage Employee Bank Details
        </h2>

        {/* MESSAGE */}
        {msg && (
          <div
            className={`mb-4 p-3 rounded text-white ${
              msg.startsWith("success") ? "bg-green-600" : "bg-red-600"
            }`}
          >
            {msg.replace("success: ", "").replace("error: ", "")}
          </div>
        )}

        {/* ================= SEARCHABLE SELECT ================= */}
        <div className="mb-5 relative" ref={dropdownRef}>
          <label className="block text-sm font-medium mb-1">
            Select Employee
          </label>

          <div
            onClick={() => setOpen(!open)}
            className="w-full border p-2 rounded cursor-pointer bg-white"
          >
            {selectedEmployee
              ? `${selectedEmployee.first_name} ${selectedEmployee.last_name} (ID: ${selectedEmployee.id})`
              : "Select Employee"}
          </div>

          {open && (
            <div className="absolute z-20 w-full bg-white border rounded mt-1 shadow-lg">
              <input
                type="text"
                placeholder="Search employee..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full p-2 border-b outline-none"
              />

              <div className="max-h-52 overflow-y-auto">
                {filteredEmployees.length === 0 && (
                  <div className="p-2 text-gray-500">
                    No employee found
                  </div>
                )}

                {filteredEmployees.map(emp => (
                  <div
                    key={emp.id}
                    onClick={() => {
                      setEmployeeId(emp.id);
                      setOpen(false);
                      setSearch("");
                      setMsg("");
                      resetBank();
                      loadBank(emp.id);
                    }}
                    className="p-2 hover:bg-indigo-100 cursor-pointer"
                  >
                    {emp.first_name} {emp.last_name} (ID: {emp.id})
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* ================= BANK FORM ================= */}
        {employeeId && (
          <>
            {loading ? (
              <p className="text-gray-500">Loading bank details...</p>
            ) : (
              <form
                onSubmit={saveBank}
                className="grid grid-cols-1 sm:grid-cols-2 gap-4"
              >
                <Field
                  label="Account Holder Name"
                  value={bank.account_holder}
                  onChange={(e) =>
                    setBank({ ...bank, account_holder: e.target.value })
                  }
                />
                <Field
                  label="Bank Name"
                  value={bank.bank_name}
                  onChange={(e) =>
                    setBank({ ...bank, bank_name: e.target.value })
                  }
                />
                <Field
                  label="Account Number"
                  value={bank.account_no}
                  onChange={(e) =>
                    setBank({ ...bank, account_no: e.target.value })
                  }
                />
                <Field
                  label="IFSC Code"
                  value={bank.ifsc}
                  onChange={(e) =>
                    setBank({ ...bank, ifsc: e.target.value })
                  }
                />
                <Field
                  label="UPI ID"
                  value={bank.upi_id}
                  onChange={(e) =>
                    setBank({ ...bank, upi_id: e.target.value })
                  }
                />
                <Field
                  label="Branch Name"
                  value={bank.branch_name}
                  onChange={(e) =>
                    setBank({ ...bank, branch_name: e.target.value })
                  }
                />

                <div className="sm:col-span-2 flex justify-end mt-3">
                  <button className="px-6 py-2 bg-indigo-600 text-white rounded-lg shadow hover:bg-indigo-700">
                    Save Bank Details
                  </button>
                </div>
              </form>
            )}
          </>
        )}
      </div>
    </HrLayout>
  );
}

/* ================= FIELD COMPONENT ================= */
function Field({ label, ...props }) {
  return (
    <div>
      <label className="block mb-1 text-sm font-medium">{label}</label>
      <input
        {...props}
        className="w-full border p-2 rounded focus:ring-2 focus:ring-indigo-400 outline-none"
      />
    </div>
  );
}
